(() => {
  const selector = '[data-new-products-carousel]';
  const carouselCleanups = new WeakMap();
  const maxRevealFrames = 12;

  function initCarousel(carousel) {
    if (carousel.dataset.newProductsReady === 'true') return;

    const track = carousel.querySelector('[data-new-products-track]');
    const previousButton = carousel.querySelector('[data-new-products-previous]');
    const nextButton = carousel.querySelector('[data-new-products-next]');
    if (!track || !previousButton || !nextButton) return;

    carousel.dataset.newProductsReady = 'true';

    let scrollFrame;
    let revealFrame;
    let resizeObserver;

    const updateButtonVisibility = () => {
      const cannotScroll = track.scrollWidth <= track.clientWidth + 1;
      previousButton.hidden = cannotScroll || track.scrollLeft <= 1;
      nextButton.hidden = cannotScroll || track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
    };

    const updateArrowPosition = () => {
      const firstCardMedia = track.querySelector('.card__media');
      if (!firstCardMedia) return false;

      const carouselBounds = carousel.getBoundingClientRect();
      const mediaBounds = firstCardMedia.getBoundingClientRect();
      if (mediaBounds.height <= 0) return false;

      const mediaCenter = mediaBounds.top - carouselBounds.top + mediaBounds.height / 2;
      carousel.style.setProperty('--new-products-arrow-top', `${mediaCenter}px`);
      return true;
    };

    const updateControls = () => {
      const isPositioned = updateArrowPosition();
      updateButtonVisibility();
      return isPositioned;
    };

    const handleScroll = () => {
      if (scrollFrame) return;

      scrollFrame = requestAnimationFrame(() => {
        scrollFrame = undefined;
        updateButtonVisibility();
      });
    };

    const handlePrevious = () => {
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      track.scrollBy({ left: -(track.clientWidth + gap), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    };

    const handleNext = () => {
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      track.scrollBy({ left: track.clientWidth + gap, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    };

    const handleResize = () => {
      updateControls();
    };

    const revealControls = (attempt = 0) => {
      if (updateControls() || attempt >= maxRevealFrames || track.children.length === 0) {
        carousel.classList.add('new-products__carousel--ready');
        return;
      }

      revealFrame = requestAnimationFrame(() => revealControls(attempt + 1));
    };

    previousButton.addEventListener('click', handlePrevious);
    nextButton.addEventListener('click', handleNext);
    track.addEventListener('scroll', handleScroll, { passive: true });

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(track);
    } else {
      window.addEventListener('resize', handleResize);
    }

    updateControls();
    revealFrame = requestAnimationFrame(revealControls);

    carouselCleanups.set(carousel, () => {
      previousButton.removeEventListener('click', handlePrevious);
      nextButton.removeEventListener('click', handleNext);
      track.removeEventListener('scroll', handleScroll);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', handleResize);
      if (scrollFrame) cancelAnimationFrame(scrollFrame);
      if (revealFrame) cancelAnimationFrame(revealFrame);
      carousel.classList.remove('new-products__carousel--ready');
      carousel.removeAttribute('data-new-products-ready');
      carouselCleanups.delete(carousel);
    });
  }

  function initCarousels(scope = document) {
    scope.querySelectorAll(selector).forEach(initCarousel);
  }

  function destroyCarousels(scope) {
    scope.querySelectorAll(selector).forEach((carousel) => carouselCleanups.get(carousel)?.());
  }

  document.addEventListener('DOMContentLoaded', () => initCarousels());
  document.addEventListener('shopify:section:load', (event) => initCarousels(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyCarousels(event.target));
})();
